import { useEffect, useState, type ReactNode } from 'react';
import { ConfirmAccountScreen, WebLoginScreen, type Account } from './WebLoginScreen';
import { SplashScreen } from './screens';

interface Props {
  /** The token this browser saved earlier (api/webSession.ts). */
  token: string | null;
  /** A token just handed over by the mini app in the URL fragment; trusted when the state matched. */
  handoff: { token: string; trusted: boolean } | null;
  /** Name and phone of the account behind a token, to confirm an untrusted hand-off. */
  describeAccount: (token: string) => Promise<Account>;
  /** Saves the token for a year. */
  onSignIn: (token: string) => void;
  /** Opens the mini app with a fresh state (domain/webLogin.ts). */
  onStartLogin: () => void;
  children: (token: string) => ReactNode;
}

type State =
  | { kind: 'signedIn'; token: string }
  | { kind: 'signedOut'; error?: string }
  | { kind: 'checking' }
  | { kind: 'confirm'; token: string; account: Account };

function initial(token: string | null, handoff: Props['handoff']): State {
  if (handoff?.trusted) return { kind: 'signedIn', token: handoff.token };
  if (handoff) return { kind: 'checking' };
  return token ? { kind: 'signedIn', token } : { kind: 'signedOut' };
}

/** The browser counterpart of AuthGate (#4218): no initData here, the mini app signs in and hands the token over. */
export function WebAuthGate({ token, handoff, describeAccount, onSignIn, onStartLogin, children }: Props) {
  const [state, setState] = useState<State>(() => initial(token, handoff));

  useEffect(() => {
    if (!handoff) return;
    if (handoff.trusted) {
      onSignIn(handoff.token);
      return;
    }
    let live = true;
    describeAccount(handoff.token).then(
      (account) => live && setState({ kind: 'confirm', token: handoff.token, account }),
      () => live && setState({ kind: 'signedOut', error: 'Ссылка для входа не подошла. Войдите ещё раз.' }),
    );
    return () => {
      live = false;
    };
    // The hand-off is read once before the first render and never changes.
  }, []);

  switch (state.kind) {
    case 'signedIn':
      return <>{children(state.token)}</>;
    case 'checking':
      return <SplashScreen />;
    case 'confirm':
      return (
        <ConfirmAccountScreen
          account={state.account}
          onConfirm={() => {
            onSignIn(state.token);
            setState({ kind: 'signedIn', token: state.token });
          }}
          onReject={() => setState(token ? { kind: 'signedIn', token } : { kind: 'signedOut' })}
        />
      );
    case 'signedOut':
      return <WebLoginScreen error={state.error} onStart={onStartLogin} />;
  }
}
